/**
 * @fileoverview Metrics Summary Cards
 * @description KPI cards showing total requests, avg response time, failure rate, etc.
 */

import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton } from '@mui/material';
import { formatDuration, formatCount } from '../utils/timeRangeUtils';

const StatCard = ({ title, value, subtitle, color }) => (
  <Card
    sx={{
      flex: '1 1 180px',
      minWidth: 180,
      borderTop: `3px solid ${color}`,
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    }}
  >
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color, my: 0.3 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const MetricsSummaryCards = ({ data, loading }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" width={200} height={90} />
        ))}
      </Box>
    );
  }

  const rows = data?.rows || [];
  if (!rows.length) {
    return null;
  }

  // Aggregate across all time buckets
  const totalRequests = rows.reduce((sum, r) => sum + (r.request_count || r.count || 0), 0);
  const totalFailed = rows.reduce((sum, r) => sum + (r.failed_count || r.failed || 0), 0);
  const allDurations = rows.filter((r) => r.avg_duration != null);
  const avgDuration =
    allDurations.length > 0
      ? allDurations.reduce((sum, r) => sum + r.avg_duration, 0) / allDurations.length
      : 0;
  const failureRate = totalRequests > 0 ? ((totalFailed / totalRequests) * 100).toFixed(2) : '0.00';
  const successRate = totalRequests > 0 ? (100 - parseFloat(failureRate)).toFixed(2) : '100.00';

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
      <StatCard
        title="Total Requests"
        value={formatCount(totalRequests)}
        subtitle="in selected range"
        color="#01426A"
      />
      <StatCard
        title="Avg Response Time"
        value={formatDuration(avgDuration)}
        subtitle="across all endpoints"
        color="#0097A9"
      />
      <StatCard
        title="Failure Rate"
        value={`${failureRate}%`}
        subtitle={`${formatCount(totalFailed)} failed`}
        color={parseFloat(failureRate) > 5 ? '#d32f2f' : '#2e7d32'}
      />
      <StatCard
        title="Success Rate"
        value={`${successRate}%`}
        subtitle="of all requests"
        color="#2e7d32"
      />
      <StatCard
        title="Data Points"
        value={formatCount(rows.length)}
        subtitle="time buckets"
        color="#7b1fa2"
      />
    </Box>
  );
};

export default MetricsSummaryCards;
